// Flags (D58, APS-469): Apsio's product flags as PostHog holds them, without opening PostHog. Each
// flag's state as a status word and whom it reaches (every organization at a rollout, or how many
// it names); staff turn one on or off, or add or take out an organization, in a form window under
// its row with the required Motivo (admin audit log). Apsio's own flags (the ones the code reads)
// come first; the others are PostHog's alone and shown for reference. A change made in PostHog
// after the page loaded is a conflict, not a failure: the window says so in the page's language
// and offers to reload (admin-d.md, the stale line).
import { PageHead, Section, Status } from '@apsio/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type FormEvent, Fragment, useId, useState } from 'react'
import { isApiError } from '../api/client'
import { productFlagsQuery, setProductFlagActive, setProductFlagTarget } from '../api/queries'
import { DataTable, FormWindow, IdText, ReasonField } from '../components/kit'
import { Empty, ErrorState, Loading } from '../components/states'
import { useI18n } from '../i18n'
import { PostHogSource } from './posthog'

export interface FlagRow {
  id: number
  key: string
  name: string
  active: boolean
  version: number
  known: boolean
  everyone: { rollout: number } | null
  orgs: string[]
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/

type Kind = 'active' | 'target'

/** A flag's state, as every admin status: `✓ ligada` or `– desligada`. */
export function FlagState({ active }: { active: boolean }) {
  const { t, locale } = useI18n()
  const label = t(active ? 'flags.on' : 'flags.off').toLocaleLowerCase(locale)
  return active ? (
    <Status s="ok" glyph="✓" label={label} />
  ) : (
    <Status s="mute" glyph="–" label={label} />
  )
}

/** The form window for one change, under its row: on or off, or an organization in or out. */
function FlagChange({ flag, kind, onDone }: { flag: FlagRow; kind: Kind; onDone: () => void }) {
  const { t } = useI18n()
  const client = useQueryClient()
  const [reason, setReason] = useState('')
  const [orgId, setOrgId] = useState('')
  const [on, setOn] = useState(true)
  const ids = { org: useId(), on: useId() }
  const save = useMutation({
    mutationFn: () =>
      kind === 'active'
        ? setProductFlagActive({
            flagId: flag.id,
            active: !flag.active,
            version: flag.version,
            reason,
          })
        : setProductFlagTarget({
            flagId: flag.id,
            orgId: orgId.trim(),
            on,
            version: flag.version,
            reason,
          }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['posthog', 'product-flags'] })
      onDone()
    },
  })
  const changed = isApiError(save.error, 409)
  const reload = async () => {
    await client.invalidateQueries({ queryKey: ['posthog', 'product-flags'] })
    onDone()
  }
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!changed) save.mutate()
  }
  return (
    <FormWindow
      title={
        kind === 'active'
          ? t(flag.active ? 'flags.turnOffTitle' : 'flags.turnOnTitle', { key: flag.key })
          : t('flags.targetTitle', { key: flag.key })
      }
      submitLabel={
        kind === 'active' ? t(flag.active ? 'flags.turnOff' : 'flags.turnOn') : t('flags.save')
      }
      busy={save.isPending}
      submitDisabled={changed}
      onSubmit={submit}
      onCancel={onDone}
      alert={
        changed ? (
          // A conflict, not a failure: the flag changed in PostHog after this page loaded.
          <p className="wide" role="alert">
            <Status s="miss" glyph="!" label={t('flags.changedWord')} />{' '}
            <span>{t('flags.changedBody')}</span>{' '}
            <button type="button" className="btn ghost sm" onClick={reload}>
              {t('flags.reload')}
            </button>
          </p>
        ) : save.isError ? (
          <ErrorState error={save.error} />
        ) : null
      }
    >
      {kind === 'target' ? (
        <>
          {flag.orgs.length ? (
            <div className="wide">
              <p className="lbl">{t('flags.targeted')}</p>
              <ul className="plain">
                {flag.orgs.map((o) => (
                  <li key={o}>
                    <IdText id={o} head={8} tail={4} />
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="field-d">
            <label className="lbl" htmlFor={ids.org}>
              {t('flags.orgId')}
            </label>
            <input
              id={ids.org}
              className="input mono"
              required
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              pattern={UUID.source.slice(1, -1)}
              autoComplete="off"
              spellCheck={false}
              translate="no"
            />
          </div>
          <div className="field-d">
            <label className="lbl" htmlFor={ids.on}>
              {t('flags.targetAction')}
            </label>
            <select
              id={ids.on}
              className="select"
              value={on ? 'on' : 'off'}
              onChange={(e) => setOn(e.target.value === 'on')}
            >
              <option value="on">{t('flags.add')}</option>
              <option value="off">{t('flags.remove')}</option>
            </select>
          </div>
        </>
      ) : (
        <p className="wide">{t(flag.active ? 'flags.turnOffBody' : 'flags.turnOnBody')}</p>
      )}
      <ReasonField value={reason} onChange={setReason} />
    </FormWindow>
  )
}

/** Whom a flag reaches: every organization at its rollout, or how many it names. */
export function FlagTargets({ flag }: { flag: FlagRow }) {
  const { t, number } = useI18n()
  const parts: string[] = []
  if (flag.everyone) parts.push(t('flags.everyone', { rollout: number(flag.everyone.rollout) }))
  if (flag.orgs.length) parts.push(t('flags.orgs', { n: flag.orgs.length }))
  return <>{parts.length ? parts.join(' · ') : t('flags.nobody')}</>
}

export function ProductFlagsPage() {
  const { t } = useI18n()
  const flags = useQuery(productFlagsQuery)
  const [editing, setEditing] = useState<{ id: number; kind: Kind } | null>(null)
  const open = (id: number, kind: Kind) =>
    setEditing(editing?.id === id && editing.kind === kind ? null : { id, kind })
  return (
    <main id="main" className="pg ops">
      <PageHead title={t('flags.title')} meta={t('flags.sub')} />
      <Section title={t('flags.listTitle')} meta={t('flags.listSub')}>
        {flags.isPending ? (
          <Loading />
        ) : flags.isError ? (
          <ErrorState error={flags.error} retry={() => flags.refetch()} />
        ) : (
          <PostHogSource section={flags.data} missing={t('flags.notConfigured')}>
            {flags.data.data && flags.data.data.length > 0 ? (
              <>
                <DataTable label={t('table.of', { name: t('flags.listTitle') })} minWidth={360}>
                  <thead>
                    <tr>
                      <th scope="col" className="f">
                        {t('flags.col.flag')}
                      </th>
                      <th scope="col">{t('flags.col.state')}</th>
                      <th scope="col" className="flag-reach">
                        {t('flags.col.targets')}
                      </th>
                      <th scope="col" className="last act">
                        <span className="visually-hidden">{t('flags.col.actions')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {flags.data.data.map((f) => (
                      <Fragment key={f.id}>
                        <tr>
                          <td className="f">
                            <span translate="no" className="mono">
                              {f.key}
                            </span>
                            {f.known ? null : <span className="dim"> · {t('flags.notApsio')}</span>}
                            {/* On a phone, whom it reaches goes under the key (no column). */}
                            <span className="t2 flag-reach-phone">
                              <FlagTargets flag={f} />
                            </span>
                          </td>
                          <td>
                            <FlagState active={f.active} />
                          </td>
                          <td className="flag-reach">
                            <FlagTargets flag={f} />
                          </td>
                          <td className="last act flag-acts">
                            <button
                              type="button"
                              className="btn ghost sm"
                              aria-expanded={editing?.id === f.id && editing.kind === 'active'}
                              onClick={() => open(f.id, 'active')}
                            >
                              {t(f.active ? 'flags.turnOff' : 'flags.turnOn')}
                              <span className="visually-hidden"> {f.key}</span>
                            </button>{' '}
                            <button
                              type="button"
                              className="btn ghost sm"
                              aria-expanded={editing?.id === f.id && editing.kind === 'target'}
                              onClick={() => open(f.id, 'target')}
                            >
                              {t('flags.target')}
                              <span className="visually-hidden"> {f.key}</span>
                            </button>
                          </td>
                        </tr>
                        {editing?.id === f.id ? (
                          <tr className="edit flag-edit">
                            <td colSpan={4}>
                              <FlagChange
                                key={editing.kind}
                                flag={f}
                                kind={editing.kind}
                                onDone={() => setEditing(null)}
                              />
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    ))}
                  </tbody>
                </DataTable>
                <p className="dim">{t('flags.listNote')}</p>
              </>
            ) : (
              <Empty title={t('flags.none')} />
            )}
          </PostHogSource>
        )}
      </Section>
    </main>
  )
}
